/**
 * Image and media utilities
 */

import { ImageSizes } from '../constants'

export type ImageSize = 'small' | 'medium' | 'large' | 'original'

export interface ProductImage {
  file: {
    small?: string
    medium?: string
    large?: string
    original: string
  }
  alt?: string
}

export interface Product {
  images?: ProductImage[]
  default_image?: ProductImage
  /** Older / alternate field name; some payloads use `image` instead of `default_image`. */
  image?: ProductImage
}

export interface CampaignProduct {
  image?: ProductImage
}

/**
 * Get placeholder image URL for given size.
 *
 * Self-contained data URI rather than a third-party image host: a portal page can render
 * several of these at once, and a blocked or rate-limited external request leaves a broken
 * <img> — an empty box where the customer expects a product.
 */
export function getPlaceholderImageUrl(size: ImageSize): string {
  const dimensions = ImageSizes[size]
  const width = dimensions[0] || 400
  const height = dimensions[1] || 400
  const fontSize = Math.max(12, Math.round(width / 9))

  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">` +
    `<rect width="${width}" height="${height}" fill="#e9e9e9"/>` +
    `<text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" ` +
    `font-family="system-ui, -apple-system, sans-serif" font-size="${fontSize}" fill="#8a8a8a">No Image</text>` +
    `</svg>`

  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`
}

/**
 * Get default product image from product. The API canonically returns
 * `default_image`; legacy payloads sometimes use `image`.
 */
export function getDefaultProductImage(product: Product): ProductImage | null {
  if (product.default_image) {
    return product.default_image
  }

  if (product.image) {
    return product.image
  }

  if (product.images && product.images.length > 0) {
    return product.images[0]
  }

  return null
}

/**
 * Get default product image URL for size
 */
export function getDefaultProductImageUrl(product: Product, size: ImageSize): string {
  const image = getDefaultProductImage(product)

  if (image?.file) {
    return image.file[size] || image.file.original
  }

  return getPlaceholderImageUrl(size)
}

/**
 * Get campaign product image URL
 */
export function getCampaignProductImageUrl(
  campaignProduct: CampaignProduct,
  size: ImageSize,
  product: Product | null = null
): string {
  if (campaignProduct.image?.file) {
    return campaignProduct.image.file[size] || campaignProduct.image.file.original
  }

  if (product) {
    return getDefaultProductImageUrl(product, size)
  }

  return getPlaceholderImageUrl(size)
}

/**
 * Set fallback image URL on error event.
 * First retries with the `original` size; on second failure shows the placeholder.
 */
export function setFallbackImageUrl(
  event: Event,
  item: any,
  size: ImageSize,
  isItem: boolean = false,
  useSubscription: boolean = false
): void {
  const target = event.target as HTMLImageElement & { _triedOriginal?: boolean }
  if (!target) {
    return
  }

  if (!target._triedOriginal) {
    target._triedOriginal = true
    target.src = isItem
      ? getItemImageUrl(item, 'original', useSubscription)
      : getCampaignProductImageUrl(item, 'original')
    return
  }

  target._triedOriginal = false
  target.src = getPlaceholderImageUrl(size)
}

/**
 * Get item image URL — checks variant image, campaign product image, product default, then
 * an image hung directly on the item.
 */
export function getItemImageUrl(item: any, size: ImageSize, useSubscription: boolean = false): string {
  const placeholder = getPlaceholderImageUrl(size)
  let product: any = null
  let variant: any = null

  if (useSubscription) {
    product = item?.next_cycle_product
    variant = item?.next_variant
  }

  if (!product) {
    product = item?.product || item?.campaign_product?.product
  }

  if (!variant) {
    variant = item?.variant
  }

  // Highest-priority source first. A source that resolves to nothing must fall through to
  // the next one: a product with no default image used to return the placeholder from here
  // and hide an image the item itself was carrying.
  const sources: Array<() => string | undefined> = [
    () => (variant?.image?.file ? variant.image.file[size] || variant.image.file.original : undefined),
    () =>
      item?.campaign_product && product && product.id === item.campaign_product.product_id
        ? getCampaignProductImageUrl(item.campaign_product, size, product)
        : undefined,
    () => (product ? getDefaultProductImageUrl(product, size) : undefined),
    () => (item?.campaign_product ? getCampaignProductImageUrl(item.campaign_product, size) : undefined),
    () => (item?.image?.file ? item.image.file[size] || item.image.file.original : undefined),
  ]

  for (const source of sources) {
    const url = source()
    if (url && url !== placeholder) {
      return url
    }
  }

  return placeholder
}

/**
 * Preload image
 */
export function preloadImage(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve()
    img.onerror = reject
    img.src = src
  })
}

/**
 * Get image dimensions
 */
export function getImageDimensions(src: string): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight })
    img.onerror = reject
    img.src = src
  })
}

const PAYMENT_BRANDS = new Set(['visa', 'mastercard', 'amex', 'discover', 'diners', 'jcb'])

/**
 * Returns the full asset path for a card brand icon.
 * Known brands → /assets/images/payment-brands/{brand}.svg
 * Unknown/fallback → /assets/icons/credit-card.svg
 */
export const getCardIconPath = (brand: string): string => {
  const normalized = brand?.toLowerCase() || ''
  if (PAYMENT_BRANDS.has(normalized)) {
    return `/assets/images/payment-brands/${normalized}.svg`
  }
  return '/assets/icons/credit-card.svg'
}
